// src/pages/RegisterPage.jsx

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { initializeRegistration, finalizeRegistration } from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { HiOutlineEye, HiOutlineEyeOff, HiOutlineLockClosed, HiOutlineMail } from 'react-icons/hi';
import logo from '../assets/logo_logo.svg';
import LanguageToggle from '../components/ui/LanguageToggle';
import ThemeToggle from '../components/ui/ThemeToggle';
import {
  clearStoredReferralCode,
  getStoredReferralCode,
  normalizeReferralCode,
  readReferralFromSearch,
  storeReferralCode
} from '../utils/referral';

const RegisterPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasReferral, setHasReferral] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const { login } = useAuth();
  const location = useLocation();
  const referralFromState = location.state?.ref;

  useEffect(() => {
    const fromState = normalizeReferralCode(referralFromState);
    const fromQuery = normalizeReferralCode(readReferralFromSearch(location.search));
    const stored = getStoredReferralCode();
    const resolved = fromState || fromQuery || stored;

    if (resolved) {
      setHasReferral(true);
      setReferralCode(resolved);
      storeReferralCode(resolved);
    }
  }, [location.search, referralFromState]);

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.register.errors.passwords_mismatch'));
      return;
    }

    const referralToSend = hasReferral ? normalizeReferralCode(referralCode) : '';
    if (hasReferral && !referralToSend) {
      setError(t('auth.register.errors.referral_required'));
      return;
    }

    setIsLoading(true);
    try {
      const initRes = await initializeRegistration(formData.email, formData.password, referralToSend || undefined);
      const confirmationToken = initRes?.confirmationToken;

      if (!confirmationToken) {
        throw new Error(t('auth.register.errors.token_missing'));
      }

      await finalizeRegistration(confirmationToken, referralToSend || undefined);
      await login(formData.email, formData.password);
      clearStoredReferralCode();
    } catch (err) {
      setError(err.message || t('auth.register.unexpected_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReferralToggle = (event) => {
    const nextValue = event.target.checked;
    setHasReferral(nextValue);

    if (!nextValue) {
      setReferralCode('');
      clearStoredReferralCode();
    } else if (referralCode) {
      storeReferralCode(referralCode);
    }
  };

  const handleReferralChange = (event) => {
    const nextValue = event.target.value;
    setReferralCode(nextValue);
    storeReferralCode(nextValue);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.18),_transparent_45%)]"></div>
        <div className="absolute -top-20 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-600/20 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-slate-700/30 blur-3xl"></div>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.85),rgba(2,6,23,0.98))]"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-slate-700/70 bg-slate-900/70 shadow-2xl shadow-blue-950/40 backdrop-blur">
            <div className="px-6 pt-8 pb-7 sm:px-8">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-700/80 bg-slate-800/80">
                  <img src={logo} alt="Hedger logo" className="h-9 w-9" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    {t('auth.register.title')}
                  </h1>
                  <p className="text-sm text-slate-400">
                    {t('auth.register.subtitle')}
                  </p>
                </div>
              </div>

              <form className="mt-7 space-y-5" onSubmit={handleRegister}>
                <div className="relative">
                  <HiOutlineMail className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email-address"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                    required
                    className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 py-3 pl-11 pr-4 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    placeholder={t('auth.common.email_placeholder')}
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="relative">
                  <HiOutlineLockClosed className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 py-3 pl-11 pr-12 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    placeholder={t('auth.common.password_placeholder')}
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute top-1/2 right-3 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-200"
                    aria-label={showPassword ? t('auth.common.hide_password') : t('auth.common.show_password')}
                  >
                    {showPassword ? <HiOutlineEyeOff className="h-5 w-5" /> : <HiOutlineEye className="h-5 w-5" />}
                  </button>
                </div>

                <div className="relative">
                  <HiOutlineLockClosed className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="confirm-password"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 py-3 pl-11 pr-12 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    placeholder={t('auth.register.confirm_password_placeholder')}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute top-1/2 right-3 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-200"
                    aria-label={showPassword ? t('auth.common.hide_password') : t('auth.common.show_password')}
                  >
                    {showPassword ? <HiOutlineEyeOff className="h-5 w-5" /> : <HiOutlineEye className="h-5 w-5" />}
                  </button>
                </div>

                <div className="rounded-2xl border border-slate-800/80 bg-slate-900/50 px-4 py-4">
                  <label className="flex items-start gap-3 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={hasReferral}
                      onChange={handleReferralToggle}
                      className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500/40"
                    />
                    <span>{t('auth.register.have_referral')}</span>
                  </label>

                  {hasReferral && (
                    <div className="mt-4 space-y-2">
                      <label className="text-xs uppercase tracking-wider text-slate-500">{t('auth.register.referral_label')}</label>
                      <input
                        type="text"
                        autoComplete="off"
                        required={hasReferral}
                        value={referralCode}
                        onChange={handleReferralChange}
                        placeholder={t('auth.register.referral_placeholder')}
                        className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 px-4 py-3 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      />
                      <p className="text-xs text-slate-500">
                        {t('auth.register.referral_hint')}
                      </p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-sky-500 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition-all hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-blue-400/40 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading ? t('auth.register.processing') : t('auth.register.submit')}
                </button>
              </form>

              {error && (
                <div className="mt-5 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                  {error}
                </div>
              )}

              <div className="mt-6 border-t border-slate-800/80 pt-5 text-center text-sm text-slate-400">
                {t('auth.register.have_account')}{' '}
                <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">
                  {t('auth.register.sign_in')}
                </Link>
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            {t('auth.register.footer')}
          </p>
        </div>
      </div>

      <div className="fixed bottom-3 right-3 z-20 flex w-44 gap-2 sm:bottom-4 sm:right-4 sm:w-52">
        <ThemeToggle shortLabels size="sm" className="shadow-lg shadow-black/35" />
        <LanguageToggle shortLabels size="sm" className="shadow-lg shadow-black/35" />
      </div>
    </div>
  );
};

export default RegisterPage;
